import React, { useState, useRef, useEffect } from 'react';
import { FaRobot, FaPaperPlane, FaTimes, FaSearch, FaBars, FaTrash } from 'react-icons/fa';
import { BiBookOpen, BiHelpCircle, BiUserCircle, BiCreditCardFront } from 'react-icons/bi';
// import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// import { faHandshake } from '@fortawesome/free-solid-svg-icons';
// import Swal from 'sweetalert2';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import { getProfilePictureUrl } from '../../utils/apiHelper';
import './AIAssistant.css';

// Inline styles for sitemap and navigation elements
const styles = {
  sitemapContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '15px',
    marginTop: '15px',
  },
  sitemapCategory: {
    width: 'calc(50% - 10px)',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    padding: '10px',
    marginBottom: '10px',
  },
  categoryName: {
    fontWeight: 'bold',
    marginBottom: '8px',
    color: '#2c3e50',
    borderBottom: '1px solid #ddd',
    paddingBottom: '5px',
  },
  categoryPages: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
  },
  pageLinkButton: {
    background: 'white',
    border: '1px solid #ddd',
    borderRadius: '5px',
    padding: '6px 10px',
    textAlign: 'left',
    cursor: 'pointer',
    transition: 'all 0.2s',
    color: '#3498db',
    fontSize: '0.9rem',
  },
  navigationMessage: {
    backgroundColor: '#e8f4fc',
    borderLeft: '4px solid #3498db',
  },
};

const AIAssistant = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const { token, profile } = useAuth();
  const navigate = useNavigate();
  const [userCourses, setUserCourses] = useState([]);
  const [courseProgress, setCourseProgress] = useState({});
  const [userName, setUserName] = useState('Learner');
  const [warningDisplayed, setWarningDisplayed] = useState(false);
  const [quickAccessVisible, setQuickAccessVisible] = useState(false);
  const [learningTips, setLearningTips] = useState([]);
  const [_enrollmentSteps, _setEnrollmentSteps] = useState(null);
  const [_currentLocation, setCurrentLocation] = useState('/');
  const [courseAnalytics, setCourseAnalytics] = useState({});
  const [learningPathways, setLearningPathways] = useState([]);
  const [upcomingDeadlines, setUpcomingDeadlines] = useState([]);
  const [recommendedResources, setRecommendedResources] = useState([]);
  const [courseCatalogData, setCourseCatalogData] = useState({});
  const [communityStats, setCommunityStats] = useState({});
  const [userPreferences, setUserPreferences] = useState({});
  const [_searchHistory, _setSearchHistory] = useState([]);
  const [_availablePages, _setAvailablePages] = useState([]);
  const [_wishlist, setWishlist] = useState([]);
  const [cart, setCart] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [_searchResults, _setSearchResults] = useState([]);
  const [_profilePicture, setProfilePicture] = useState();
  const [_subscriptions, setSubscriptions] = useState([]);
  const [_conversations, setConversations] = useState([]);
  const [_performanceData, setPerformanceData] = useState({});
  const [userRoles, setUserRoles] = useState([]);
  const [_blockedRoles, setBlockedRoles] = useState([]);
  const [_showCategories, _setShowCategories] = useState(false);
  const [categories, setCategories] = useState([]);
  const [_suggestedCourses, setSuggestedCourses] = useState([]);
  const [_instructorStatus, setInstructorStatus] = useState(null);
  const [_feedback, setFeedback] = useState([]);
  const [_sponsors, setSponsors] = useState([]);
  const [_homeContent, setHomeContent] = useState({});
  const [_logo, setLogo] = useState(null);
  const [_defaultProfilePic, setDefaultProfilePic] = useState(null);
  const [_menuItems, _setMenuItems] = useState([]);
  const [_searchSuggestions, _setSearchSuggestions] = useState([]);
  const [_isLoggedIn, _setIsLoggedIn] = useState(false);
  const [_profileLoading, _setProfileLoading] = useState(false);
  const [_statusChecked, _setStatusChecked] = useState(false);
  const [_instructorCourses, setInstructorCourses] = useState([]);
  const [_lastActivity, setLastActivity] = useState(null);
  const [_autoSuggestions, setAutoSuggestions] = useState([]);
  const [_isInstructorAdded, setIsInstructorAdded] = useState(false);

  // Add a useEffect to initialize available pages from the router
  useEffect(() => {
    // Update available pages if learning pathways are removed
    if (!hasEnoughDataToRespond(learningPathways) || learningPathways.length === 0) {
      // Update learning pathways related responses to not mention them
      const learningPathwaysReplacement = {
        text: 'We\'re currently working on building personalized learning pathways based on your interests and goals. In the meantime, I can help you find courses that align with your learning objectives.',
        sender: 'ai',
        timestamp: new Date().toLocaleTimeString(),
        suggestions: [
          { text: 'Find Courses', action: 'Navigate to courses' },
          { text: 'Course Recommendations', action: 'What courses do you recommend for me?' },
          { text: 'Learning Preferences', action: 'How do I set my learning preferences?' },
        ],
      };

      // Store this replacement for use in the processMessage function
      sessionStorage.setItem('learningPathwaysReplacement', JSON.stringify(learningPathwaysReplacement));
    }
  }, [learningPathways]);

  // Add useEffect to load chat history from localStorage on component mount
  useEffect(() => {
    // Load saved messages from localStorage if they exist
    const savedMessages = localStorage.getItem('lms-chat-history');
    if (savedMessages) {
      try {
        const parsedMessages = JSON.parse(savedMessages);
        setMessages(parsedMessages);
      } catch (error) {
        // console.error('Error parsing saved chat history:', error);
        // If there's an error parsing, reset the localStorage
        localStorage.removeItem('lms-chat-history');
      }
    } else if (isOpen && !warningDisplayed) {
      // Only set welcome message if no saved messages
      const welcomeMessage = {
        text: `Hello ${userName}! I'm your UlsaApp Assistant. I can help you with:\n\nMore!\n\nWhat would you like to do today?`,
        sender: 'ai',
        timestamp: new Date().toLocaleTimeString(),
      };

      setMessages([welcomeMessage]);
      setWarningDisplayed(true);
    }
  }, [isOpen, warningDisplayed, userName]);

  // Add useEffect to save messages to localStorage whenever they change
  useEffect(() => {
    if (messages.length > 0) {
      localStorage.setItem('lms-chat-history', JSON.stringify(messages));
    }
  }, [messages]);

  // Add a function to clear chat history
  const clearChatHistory = () => {
    setMessages([]);
    localStorage.removeItem('lms-chat-history');

    // Add welcome message back after clearing
    const welcomeMessage = {
      text: `Hello ${userName}! I'm your UlsaApp Assistant. I can help you with:\n\nWhat would you like to do today?`,
      sender: 'ai',
      timestamp: new Date().toLocaleTimeString(),
    };

    setMessages([welcomeMessage]);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && !warningDisplayed) {
      setMessages([{
        text: '⚠️UlsaApp Assistant is currently under development.\nYou may not get all answers as some features are still being implemented.',
        sender: 'ai',
        timestamp: new Date().toLocaleTimeString(),
      }]);
      setWarningDisplayed(true);
    }
  }, [isOpen, warningDisplayed]);

  // Fetch comprehensive user data
  useEffect(() => {
    const fetchUserData = async () => {
      if (!token) return;

      try {
        // Ensure `api.getPurchasedCourses` is defined
        if (!api.getPurchasedCourses) {
          return; // Silently skip if endpoint is missing
        }

        // Fetch user profile and roles
        if (profile) {
          const name = profile.name || 'Learner';
          setUserName(name);
          setProfilePicture(
            getProfilePictureUrl(profile.profilePicture, api.img, null, name),
          );
          setUserRoles(profile.role || []);
          setBlockedRoles(profile.blocked || []);
        }

        // Fetch purchased courses
        const coursesResponse = await axios.get(api.getPurchasedCourses, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const courses = coursesResponse.data.courses || [];
        setUserCourses(courses);

        // Calculate progress for each course
        const progressData = {};
        courses.forEach(course => {
          const watchedVideos = localStorage.getItem(`watchedVideos_${course._id}`) || '{}';
          const videoProgress = localStorage.getItem(`videoProgress_${course._id}`) || '{}';

          let watchedSeconds = 0;
          Object.values(JSON.parse(videoProgress)).forEach(progress => {
            watchedSeconds += progress.watchedDuration || 0;
          });

          const totalSeconds = course.totalHours ? course.totalHours * 3600 : 0;
          const progressPercent = totalSeconds > 0
            ? Math.min(100, Math.round((watchedSeconds / totalSeconds) * 100))
            : 0;

          const courseKey = `${course.title}_${course.category}`;
          progressData[courseKey] = progressPercent;
        });
        setCourseProgress(progressData);

        // Fetch cart and wishlist
        if (!api.getCartAndWishlist) {
          return; // Silently skip if endpoint is missing
        }
        const cartWishlistResponse = await axios.get(api.getCartAndWishlist, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCart(cartWishlistResponse.data.cart || []);
        setWishlist(cartWishlistResponse.data.wishlist || []);

        // Fetch notifications
        if (!api.getNotifications) {
          return; // Silently skip if endpoint is missing
        }
        const notificationsResponse = await axios.get(api.getNotifications, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setNotifications(notificationsResponse.data.notifications || []);

        // Fetch subscriptions
        if (!api.getSubscriptions) {
          return; // Silently skip if endpoint is missing
        }
        const subscriptionsResponse = await axios.get(api.getSubscriptions, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSubscriptions(subscriptionsResponse.data.subscriptions || []);

        // Fetch conversations
        if (!api.getConversations) {
          return; // Silently skip if endpoint is missing
        }
        const conversationsResponse = await axios.get(api.getConversations, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setConversations(conversationsResponse.data.conversations || []);

        // Fetch performance data if user is an instructor
        if (userRoles.includes('instructor')) {
          if (!api.getPerformance) {
            return; // Silently skip if endpoint is missing
          }
          const performanceResponse = await axios.get(api.getPerformance, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setPerformanceData(performanceResponse.data || {});
        }
      } catch (error) {
        // Silently ignore errors
      }
    };

    fetchUserData();
  }, [token, profile, userRoles]);

  // Fetch additional user data
  useEffect(() => {
    const fetchAdditionalData = async () => {
      if (!token) return;

      try {
        // Fetch categories
        if (!api.getAllCategories) {
          return; // Silently skip if endpoint is missing
        }
        const categoriesResponse = await axios.get(api.getAllCategories, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCategories(categoriesResponse.data.categories || []);

        // Fetch suggested courses
        if (!api.getSuggestedCourses) {
          return; // Silently skip if endpoint is missing
        }
        const suggestedResponse = await axios.get(api.getSuggestedCourses, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSuggestedCourses(suggestedResponse.data.courses || []);

        // Fetch instructor status
        if (profile?._id && api.checkInstructorSubmissionStatus) {
          const instructorResponse = await axios.get(api.checkInstructorSubmissionStatus(profile._id), {
            headers: { Authorization: `Bearer ${token}` },
          });
          setInstructorStatus(instructorResponse.data.status || null);
        }

        // Fetch user feedback
        if (!api.getUserFeedback) {
          return; // Silently skip if endpoint is missing
        }
        const feedbackResponse = await axios.get(api.getUserFeedback, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setFeedback(feedbackResponse.data.feedback || []);

        // Fetch sponsors
        if (!api.getSponsors) {
          return; // Silently skip if endpoint is missing
        }
        const sponsorsResponse = await axios.get(api.getSponsors);
        setSponsors(sponsorsResponse.data.sponsors || []);

        // Fetch home content
        if (!api.getHomeContent) {
          return; // Silently skip if endpoint is missing
        }
        const homeContentResponse = await axios.get(api.getHomeContent);
        setHomeContent(homeContentResponse.data || {});

        // Fetch logo and default profile picture
        setLogo(api.img + 'logo.png');
        setDefaultProfilePic(api.img + 'defaultProfilePic.png');
      } catch (error) {
        // Silently ignore errors
      }
    };

    fetchAdditionalData();
  }, [token, profile]);

  // Fetch instructor courses
  useEffect(() => {
    const fetchInstructorCourses = async () => {
      if (!token || !userRoles.includes('instructor')) return;

      try {
        if (!api.getInstructorCourses) {
          return; // Silently skip if endpoint is missing
        }
        if (!profile || !profile._id) {
          return; // No profile or id available
        }
        const response = await axios.get(api.getInstructorCourses(profile._id), {
          headers: { Authorization: `Bearer ${token}` },
        });
        setInstructorCourses(response.data.courses || []);
      } catch (error) {
        // Silently ignore errors
      }
    };

    fetchInstructorCourses();
  }, [token, userRoles, profile]);

  // Add new useEffect for automatic suggestions
  useEffect(() => {
    const generateAutoSuggestions = () => {
      const suggestions = [];

      // Check for incomplete courses
      const incompleteCourses = userCourses.filter(course => {
        const courseKey = `${course.title}_${course.category}`;
        return courseProgress[courseKey] < 100;
      });

      if (incompleteCourses.length > 0) {
        suggestions.push({
          type: 'course_progress',
          message: `You have ${incompleteCourses.length} courses to complete. Would you like to continue learning?`,
          action: () => navigate('/learning'),
        });
      }

      // Check for items in cart
      if (cart.length > 0) {
        suggestions.push({
          type: 'cart',
          message: `You have ${cart.length} items in your cart. Would you like to complete your purchase?`,
          action: () => navigate('/cart'),
        });
      }

      // Check for new notifications
      if (notifications.length > 0) {
        suggestions.push({
          type: 'notifications',
          message: `You have ${notifications.length} new notifications. Would you like to check them?`,
          action: () => navigate('/notifications'),
        });
      }

      // Check for upcoming course deadlines
      const upcomingDeadlines = userCourses.filter(course => {
        const deadline = new Date(course.deadline);
        const now = new Date();
        const daysUntilDeadline = Math.ceil((deadline - now) / (1000 * 60 * 60 * 24));
        return daysUntilDeadline <= 7 && daysUntilDeadline > 0;
      });

      if (upcomingDeadlines.length > 0) {
        suggestions.push({
          type: 'deadlines',
          message: `You have ${upcomingDeadlines.length} courses with upcoming deadlines. Would you like to check them?`,
          action: () => navigate('/learning'),
        });
      }

      setAutoSuggestions(suggestions);
    };

    if (isOpen) {
      generateAutoSuggestions();
    }
  }, [isOpen, userCourses, courseProgress, cart, notifications, navigate]);

  // Add new function for automatic notifications
  const _sendAutomaticNotification = (type, message) => {
    const notification = {
      text: message,
      sender: 'ai',
      timestamp: new Date().toLocaleTimeString(),
      type: type,
    };

    setMessages(prev => [...prev, notification]);
    scrollToBottom();
  };

  // Add a function to check if input is valid
  const isValidInput = (input) => {
    // Check for empty input or input with only spaces/special characters
    if (!input || input.trim().length === 0) return false;

    // Check for excessively long inputs that might be spam
    if (input.length > 500) return false;

    // Check for repeated characters that might indicate spam
    const repeatedCharsRegex = /(.)\1{10,}/;
    if (repeatedCharsRegex.test(input)) return false;

    return true;
  };

  // Create a helper function to extract course names from messages
  const extractCourseName = (message) => {
    // Try to find course name after common phrases
    const patterns = [
      /about\s+(?:the\s+)?course\s+"?([^"]+)"?/i,
      /for\s+(?:the\s+)?course\s+"?([^"]+)"?/i,
      /course\s+(?:called|named|titled)\s+"?([^"]+)"?/i,
      /progress\s+(?:for|in|on)\s+"?([^"]+)"?/i,
      /in\s+"?([^"]+)"?\s+course/i,
    ];

    for (const pattern of patterns) {
      const match = message.match(pattern);
      if (match && match[1]) {
        return match[1].trim();
      }
    }

    // Fallback to looking for course followed by a word 
    const courseWordMatch = message.match(/course\s+([a-zA-Z0-9]+)/i);
    if (courseWordMatch && courseWordMatch[1]) {
      return courseWordMatch[1].trim();
    }

    return null;
  };

  // Add this after the extractCourseName function
  const extractSearchTerm = (message) => {
    // Match patterns like "search for X" or "find X"
    const searchPhrases = [
      /search\s+for\s+["']?([^"']+)["']?/i,
      /search\s+["']?([^"']+)["']?/i,
      /find\s+["']?([^"']+)["']?/i,
      /look\s+for\s+["']?([^"']+)["']?/i,
      /searching\s+for\s+["']?([^"']+)["']?/i,
    ];

    for (const phrase of searchPhrases) {
      const match = message.match(phrase);
      if (match && match[1]) {
        return match[1].trim();
      }
    }

    // If no match with specific phrases, try to find words after search-related words
    const words = message.split(' ');
    const searchIndex = words.findIndex(word =>
      word.toLowerCase() === 'find' ||
            word.toLowerCase() === 'search' ||
            word.toLowerCase() === 'query',
    );

    if (searchIndex !== -1 && searchIndex < words.length - 1) {
      return words.slice(searchIndex + 1).join(' ');
    }

    return null;
  };

  // Enhance processMessage with better organization and comprehensive suggestions
  const processMessage = async (message) => {
    const lowerMessage = message.toLowerCase();

    // Create a helper function to generate standardized suggestions by category
    const _getServiceSuggestions = (primaryCategory) => {
      // Common service categories with their actions
      const serviceCategories = {
        learning: [
          { text: 'My Courses', action: 'Show my courses' },
          { text: 'Track Progress', action: 'Show my learning progress' },
          { text: 'Certificates', action: 'Show my certificates' },
        ],
        courses: [
          { text: 'Browse Courses', action: 'Show popular courses' },
          { text: 'Course Categories', action: 'Show course categories' },
          { text: 'Free Courses', action: 'Show free courses' },
        ],
        account: [
          { text: 'Edit Profile', action: 'How do I edit my profile?' },
          { text: 'Account Settings', action: 'Navigate to account-settings' },
          { text: 'Password Help', action: 'How do I change my password?' },
        ],
        payments: [
          { text: 'Payment Methods', action: 'What payment methods are accepted?' },
          { text: 'Checkout Help', action: 'Help with checkout' },
          { text: 'Refund Policy', action: 'What is the refund policy?' },
        ],
        technical: [
          { text: 'Technical Support', action: 'I need technical help' },
          { text: 'Mobile App', action: 'Tell me about the mobile app' },
          { text: 'System Requirements', action: 'What are the system requirements?' },
        ],
        community: [
          { text: 'Discussion Forums', action: 'Navigate to forums' },
          { text: 'Study Groups', action: 'How do I join study groups?' },
          { text: 'Contact Instructor', action: 'How do I contact an instructor?' },
        ],
      };

      // Get the primary category suggestions
      let suggestions = [...serviceCategories[primaryCategory] || []];

      // Always add help option
      suggestions.push({ text: 'Other Help Topics', action: 'What else can you help with?' });

      return suggestions;
    };

    // Enhanced course progress related queries
    if (lowerMessage.includes('progress') || lowerMessage.includes('completed')) {
      const courseName = extractCourseName(message);
      if (courseName) {
        const course = userCourses.find(c => c.title.toLowerCase().includes(courseName.toLowerCase()));
        if (course) {
          const courseKey = `${course.title}_${course.category}`;
          const progress = courseProgress[courseKey] || 0;
          const sanitizedCourse = {
            title: course.title,
            description: course.description || 'No description available',
            category: course.category || 'Uncategorized',
            totalLessons: course.totalLessons || 0,
            instructor: course.instructor || 'Unknown instructor',
            level: course.level || 'Not specified',
            lastAccessed: course.lastAccessed ? new Date(course.lastAccessed).toLocaleDateString() : 'Not accessed yet',
          };
          return {
            text: `Your progress in "${sanitizedCourse.title}" is ${progress}% complete.\n\nCourse Details:\n- Title: ${sanitizedCourse.title}\n- Description: ${sanitizedCourse.description}\n- Category: ${sanitizedCourse.category}\n- Instructor: ${sanitizedCourse.instructor}\n- Level: ${sanitizedCourse.level}\n- Total Lessons: ${sanitizedCourse.totalLessons}\n- Last Accessed: ${sanitizedCourse.lastAccessed}`,
            sender: 'ai',
            timestamp: new Date().toLocaleTimeString(),
            suggestions: [
              { text: 'Continue Learning', action: 'Navigate to learning' },
              { text: 'View Lessons', action: `Show lessons for ${sanitizedCourse.title}` },
              { text: 'Similar Courses', action: `Find similar courses to ${sanitizedCourse.title}` },
              { text: 'Contact Instructor', action: 'How do I contact the instructor?' },
              { text: 'Course Certificate', action: 'How do I get my certificate?' },
            ],
          };
        }
      }

      // If no specific course found, show progress for all courses
      const sanitizedCourses = userCourses.map(course => {
        const courseKey = `${course.title}_${course.category}`;
        const progress = courseProgress[courseKey] || 0;
        return {
          title: course.title,
          progress: progress,
          category: course.category || 'Uncategorized',
          instructor: course.instructor || 'Unknown instructor',
          level: course.level || 'Not specified',
        };
      });

      // Sort courses by progress (descending) to show most complete first
      sanitizedCourses.sort((a, b) => b.progress - a.progress);

      const courseListText = sanitizedCourses.length > 0
        ? sanitizedCourses.map(course =>
          `- ${course.title}\n  Category: ${course.category}\n  Progress: ${course.progress}%`,
        ).join('\n\n')
        : 'You haven\'t enrolled in any courses yet.';

      return {
        text: `Your Learning Progress:\n\n${courseListText}`,
        sender: 'ai',
        timestamp: new Date().toLocaleTimeString(),
        suggestions: [
          { text: 'Continue Learning', action: 'Navigate to learning' },
          { text: 'Learning Statistics', action: 'Navigate to learning-statistics' },
          { text: 'Find New Courses', action: 'Navigate to courses' },
          { text: 'Certificates', action: 'Navigate to certificates' },
          { text: 'Learning Schedule', action: 'How do I create a learning schedule?' },
        ],
      };
    }

    // Enrollment guidance - comprehensive service coverage
    if (lowerMessage.includes('how to enroll') || lowerMessage.includes('sign up for course') ||
            lowerMessage.includes('join course') || lowerMessage.includes('register for course') ||
            lowerMessage.includes('enrollment process') || lowerMessage.includes('how to register') ||
            lowerMessage.includes('how do i enroll')) {

      return {
        text: 'Here\'s how to enroll in a course:\n\n1. Find a Course:\n   - Browse the catalog or use search\n   - Filter by category, level, or price\n   - Read descriptions and reviews\n\n2. Preview & Select:\n   - Watch preview videos if available\n   - Check course curriculum\n   - Verify requirements and duration\n\n3. Enroll:\n   - Click "Enroll" or "Add to Cart"\n   - For paid courses, proceed to checkout\n   - For free courses, instant enrollment\n\n4. Payment Options:\n   - Credit/debit cards\n   - PayPal\n   - Special financing if eligible\n   - Apply any coupon codes\n\n5. Start Learning:\n   - Access course immediately after enrollment\n   - Course appears in your Learning Dashboard\n   - Begin with the introduction module',
        sender: 'ai',
        timestamp: new Date().toLocaleTimeString(),
        type: 'guidance',
        suggestions: [
          { text: 'Browse Courses', action: 'Navigate to courses' },
          { text: 'Payment Methods', action: 'What payment methods are accepted?' },
          { text: 'Free Courses', action: 'Show free courses' },
          { text: 'Course Categories', action: 'Navigate to course-categories' },
          { text: 'Enrollment Issues', action: 'I\'m having trouble enrolling' },
        ],
      };
    }

    // Payment methods and billing with complete information
    if (lowerMessage.includes('payment method') || lowerMessage.includes('billing') ||
            lowerMessage.includes('how to pay') || lowerMessage.includes('accepted payment') ||
            lowerMessage.includes('credit card') || lowerMessage.includes('debit card') ||
            lowerMessage.includes('paypal')) {

      return {
        text: 'We accept the following payment methods:\n\n1. Credit/Debit Cards:\n   - Visa\n   - Mastercard\n   - American Express\n   - Discover\n\n2. Digital Wallets:\n   - PayPal\n   - Apple Pay\n   - Google Pay\n\n3. Bank Transfers:\n   - Direct bank transfer (select countries)\n   - ACH (US only)\n\n4. Alternative Methods:\n   - Installment plans for eligible courses\n   - Gift cards and promotional credits\n   - Enterprise billing (for business accounts)\n\nAll transactions are secure and encrypted. Your payment details are never stored on our servers.',
        sender: 'ai',
        timestamp: new Date().toLocaleTimeString(),
        suggestions: [
          { text: 'Add Payment Method', action: 'How do I add a payment method?' },
          { text: 'Checkout Help', action: 'Help with checkout process' },
          { text: 'Refund Policy', action: 'What is the refund policy?' },
          { text: 'Billing Issues', action: 'I have a billing problem' },
          { text: 'Course Discounts', action: 'Are there any discounts available?' },
        ],
      };
    }

    // Course categories with comprehensive listing
    if (lowerMessage.includes('category') || lowerMessage.includes('categories')) {
      const sanitizedCategories = categories.map(category => ({
        name: category.name || 'Uncategorized',
        description: category.description || 'No description available',
        courseCount: category.courseCount || 0,
        subcategories: category.subcategories || [],
      }));

      // List top categories with course counts
      const topCategories = sanitizedCategories.slice(0, 6);
      const categoryText = topCategories.map(category =>
        `- ${category.name} (${category.courseCount} courses)`,
      ).join('\n');

      // Include total category count
      const totalCategories = sanitizedCategories.length;

      return {
        text: `Course Categories:\n\nPopular categories:\n${categoryText}\n\nWe offer ${totalCategories} categories covering technical, business, creative, and personal development topics.`,
        sender: 'ai',
        timestamp: new Date().toLocaleTimeString(),
        suggestions: [
          ...topCategories.slice(0, 3).map(category => ({
            text: category.name,
            action: `Show courses in ${category.name}`,
          })),
          { text: 'All Categories', action: 'Navigate to course-categories' },
          { text: 'Popular Courses', action: 'Show popular courses' },
          { text: 'Free Courses', action: 'Show free courses' },
        ],
      };
    }

    // Certificate guidance with complete service information
    if (lowerMessage.includes('certificate') || lowerMessage.includes('certification') ||
            (lowerMessage.includes('proof') && lowerMessage.includes('completion'))) {

      const certificateSuggestions = [
        { text: 'My Certificates', action: 'Navigate to certificates' },
        { text: 'Certificate Verification', action: 'How can others verify my certificate?' },
        { text: 'LinkedIn Sharing', action: 'How do I share to LinkedIn?' },
        { text: 'Course Completion', action: 'What\'s required to complete a course?' },
        { text: 'Print Certificate', action: 'How do I print my certificate?' },
      ];

      return {
        text: 'Certificates are awarded when you complete courses. Here\'s everything you need to know:\n\n1. Earning Certificates:\n   - Complete all required course content (usually 80-100%)\n   - Pass any required final assessments or projects\n   - Certificates generate automatically upon completion\n\n2. Accessing Your Certificates:\n   - Go to "My Learning" in the navigation menu\n   - Select the "Certificates" tab\n   - All earned certificates appear in chronological order\n\n3. Certificate Features:\n   - Professional design with course details\n   - Your verified name as registered\n   - Unique certificate ID for verification\n   - Course completion date\n\n4. Using Your Certificates:\n   - Download as PDF for printing\n   - Share directly to LinkedIn with one click\n   - Get shareable links for social media or email',
        sender: 'ai',
        timestamp: new Date().toLocaleTimeString(),
        type: 'guidance',
        suggestions: filterSuggestions(certificateSuggestions),
      };
    }

    // Account and profile management with complete options
    if (lowerMessage.includes('profile') || lowerMessage.includes('account')) {
      if (lowerMessage.includes('edit') || lowerMessage.includes('update') || lowerMessage.includes('change')) {
        return {
          text: 'To edit your profile:\n\n1. Click on your profile picture in the top right\n2. Select "Edit Profile" from the dropdown menu\n3. Update your information:\n   - Profile picture\n   - Display name\n   - Biography\n   - Social media links\n   - Professional information\n4. Click "Save Changes" when finished\n\nYour updated profile will be visible to instructors and other students (if you\'ve enabled profile visibility).',
          sender: 'ai',
          timestamp: new Date().toLocaleTimeString(),
          type: 'guidance',
          suggestions: [
            { text: 'Go to Edit Profile', action: 'Navigate to edit-profile' },
            { text: 'Privacy Settings', action: 'How do I change privacy settings?' },
            { text: 'Profile Picture', action: 'How do I change my profile picture?' },
            { text: 'Account Settings', action: 'Navigate to account-settings' },
            { text: 'Delete Account', action: 'How do I delete my account?' },
          ],
        };
      }

      const sanitizedProfile = {
        name: profile?.name || 'Learner',
        email: profile?.email || 'Not provided',
        role: profile?.role?.join(', ') || 'No roles assigned',
        joinedDate: profile?.createdAt ? new Date(profile.createdAt).toLocaleDateString() : 'Not available',
        lastLogin: profile?.lastLogin ? new Date(profile.lastLogin).toLocaleDateString() : 'Not available',
      };

      return {
        text: `Your Account Information:\n\n- Name: ${sanitizedProfile.name}\n- Email: ${sanitizedProfile.email}\n- Role: ${sanitizedProfile.role}\n- Member since: ${sanitizedProfile.joinedDate}\n- Last login: ${sanitizedProfile.lastLogin}\n\nWhat would you like to manage in your account?`,
        sender: 'ai',
        timestamp: new Date().toLocaleTimeString(),
        suggestions: [
          { text: 'Edit Profile', action: 'Navigate to edit-profile' },
          { text: 'Account Settings', action: 'Navigate to account-settings' },
          { text: 'Payment Methods', action: 'Manage payment methods' },
          { text: 'Notification Settings', action: 'How do I change notification settings?' },
          { text: 'Privacy Settings', action: 'How do I change privacy settings?' },
        ],
      };
    }

    // Mobile app information with complete feature list
    if (lowerMessage.includes('mobile app') || lowerMessage.includes('iphone app') ||
            lowerMessage.includes('android app') || lowerMessage.includes('tablet app')) {

      return {
        text: 'Our Mobile Learning App:\n\n1. Availability:\n   - iOS: iPhone and iPad (iOS 13+)\n   - Android: Phones and tablets (Android 7.0+)\n   - Download from App Store or Google Play\n\n2. Key Features:\n   - Offline course viewing\n   - Video playback speed control\n   - Background audio mode\n   - Download manager with quality settings\n   - Picture-in-picture video support\n   - Course bookmarking\n   - Push notifications for updates\n\n3. Exclusive Mobile Features:\n   - Screen lock prevention during videos\n   - Download scheduling (WiFi only option)\n   - Reduced data mode\n   - Dark mode and reading themes\n   - Voice command navigation\n   - Gesture controls for video playback\n   - Accessibility voice-over support\n   - Integrated flashcard study system\n   - Offline quiz attempts\n   - Geographic learning reminders\n\n4. Security Features:\n   - Biometric login (Face/Touch ID)\n   - PIN protection option\n   - Secure document storage\n   - End-to-end encrypted notes',
        sender: 'ai',
        timestamp: new Date().toLocaleTimeString(),
        type: 'guidance',
        suggestions: [
          { text: 'iOS App', action: 'Navigate to app-store' },
          { text: 'Android App', action: 'Navigate to play-store' },
          { text: 'Offline Viewing', action: 'How does offline viewing work?' },
          { text: 'App Requirements', action: 'What are the app requirements?' },
          { text: 'App Troubleshooting', action: 'Mobile app troubleshooting' },
        ],
      };
    }

    // Technical support with comprehensive options
    if (lowerMessage.includes('technical support') || lowerMessage.includes('technical help') ||
            lowerMessage.includes('tech support') || lowerMessage.includes('help desk')) {

      return {
        text: 'Technical Support Options:\n\n1. Help Center:\n   - Searchable knowledge base\n   - Step-by-step guides\n   - Video tutorials\n   - FAQs\n\n2. Contact Support:\n   - Email support: support@learningplatform.com\n   - Chat support: Available Mon-Fri, 9am-6pm EST\n   - Support tickets: 24-48 hour response time\n\n3. Community Help:\n   - Course-specific forums\n   - Technical discussion boards\n   - Peer assistance\n\n4. System Requirements:\n   - Browser: Chrome, Firefox, Safari, Edge (latest versions)\n   - Internet: 5+ Mbps for HD video\n   - Mobile: iOS 13+ or Android 7.0+',
        sender: 'ai',
        timestamp: new Date().toLocaleTimeString(),
        type: 'guidance',
        suggestions: [
          { text: 'Help Center', action: 'Navigate to help-center' },
          { text: 'Contact Support', action: 'Navigate to contact-support' },
          { text: 'System Check', action: 'Navigate to system-check' },
          { text: 'Video Playback Issues', action: 'Help with video playback' },
          { text: 'Browser Problems', action: 'My browser isn\'t working properly' },
        ],
      };
    }

    // Course difficulty levels with comprehensive explanation
    if (lowerMessage.includes('difficulty level') || lowerMessage.includes('course level') ||
            lowerMessage.includes('beginner course') || lowerMessage.includes('advanced course') ||
            lowerMessage.includes('intermediate course')) {

      return {
        text: 'Our courses are categorized into these difficulty levels:\n\n1. Beginner Level:\n   - No prior knowledge required\n   - Starts with fundamental concepts\n   - Step-by-step guidance\n   - Basic terminology explained\n\n2. Intermediate Level:\n   - Requires basic understanding of the subject\n   - Builds on fundamental knowledge\n   - More complex concepts and techniques\n   - Some practical experience helpful\n\n3. Advanced Level:\n   - Requires strong understanding of the subject\n   - Covers complex and specialized topics\n   - Focuses on mastery and expertise\n   - Practical experience expected\n\n4. All Levels:\n   - Structured to accommodate various experience levels\n   - Begins with basics but progresses to advanced topics',
        sender: 'ai',
        timestamp: new Date().toLocaleTimeString(),
        suggestions: [
          { text: 'Beginner Courses', action: 'Show beginner level courses' },
          { text: 'Intermediate Courses', action: 'Find intermediate level courses' },
          { text: 'Advanced Courses', action: 'Show advanced level courses' },
          { text: 'All Levels Courses', action: 'Show all levels courses' },
          { text: 'Recommendations', action: 'Recommend courses for my level' },
        ],
      };
    }

    // Accessibility features with comprehensive information
    if (lowerMessage.includes('accessibility') || lowerMessage.includes('disability') ||
            lowerMessage.includes('screen reader') || lowerMessage.includes('visually impaired') ||
            lowerMessage.includes('hearing impaired')) {

      return {
        text: 'Our Accessibility Features:\n\n1. Video Content:\n   - Closed captions for all videos\n   - Transcripts for all audio content\n   - Adjustable playback speed\n   - High-contrast video player\n\n2. Navigation & Interface:\n   - Screen reader compatibility\n   - Keyboard navigation\n   - Focus indicators\n   - Resizable text\n\n3. Learning Accommodations:\n   - Extended time options for assessments\n   - Alternative format materials\n   - Text-to-speech integration\n   - Color contrast adjustments\n\nWe\'re committed to making learning accessible to everyone. If you need specific accommodations, please contact our support team.',
        sender: 'ai',
        timestamp: new Date().toLocaleTimeString(),
        type: 'guidance',
        suggestions: [
          { text: 'Accessibility Settings', action: 'Navigate to accessibility-settings' },
          { text: 'Caption Settings', action: 'How do I adjust caption settings?' },
          { text: 'Request Accommodations', action: 'How do I request accommodations?' },
          { text: 'Screen Reader Help', action: 'Help with screen readers' },
          { text: 'Contact Support', action: 'Navigate to contact-support' },
        ],
      };
    }

    // Complete list of services and capabilities
    if (lowerMessage.includes('what can you help with') || lowerMessage.includes('what can you do') ||
            lowerMessage.includes('your capabilities') || lowerMessage.includes('services available') ||
            lowerMessage.includes('available services') || lowerMessage.includes('help options')) {

      return {
        text: 'I can help you with these services:\n\n1. Learning Management:\n   - Course progress tracking\n   - Assignment help\n   - Learning schedules\n   - Study techniques\n   - Certificates\n\n2. Course Discovery:\n   - Finding courses by topic/level\n   - Course recommendations\n   - Category exploration\n   - Course comparisons\n   - Free resources\n\n3. Account Management:\n   - Profile settings\n   - Notification preferences\n   - Privacy controls\n   - Password assistance\n   - Account security\n\n4. Purchases & Enrollment:\n   - Payment methods\n   - Enrollment process\n   - Refunds and policies\n   - Discounts and coupons\n   - Subscription management\n\n5. Technical Support:\n   - Device compatibility\n   - Browser issues\n   - Video playback problems\n   - Mobile app assistance\n   - Accessibility features\n\n6. Platform Navigation:\n   - Finding specific features\n   - Learning Dashboard help\n   - Community participation\n   - Direct navigation assistance',
        sender: 'ai',
        timestamp: new Date().toLocaleTimeString(),
        type: 'guidance',
        suggestions: [
          { text: 'Course Help', action: 'I need help with courses' },
          { text: 'Account Help', action: 'Help with my account' },
          { text: 'Payment Help', action: 'Help with payments' },
          { text: 'Technical Help', action: 'I need technical support' },
          { text: 'Submit Feedback', action: 'How do I submit feedback?' },
        ],
      };
    }

    // For general navigation and help
    if (lowerMessage.includes('help') || lowerMessage.includes('guide') || lowerMessage.includes('support')) {
      // Don't trigger this for "feedback" queries as they have their own handler
      if (!lowerMessage.includes('feedback')) {
        return {
          text: 'How can I help you today? Here are the main areas I can assist with:\n\n1. Courses & Learning\n2. Account Management\n3. Payments & Enrollment\n4. Technical Support\n5. Platform Navigation\n6. Submit Feedback\n\nPlease let me know what specific area you need help with.',
          sender: 'ai',
          timestamp: new Date().toLocaleTimeString(),
          suggestions: [
            { text: 'Help with Courses', action: 'I need help with my courses' },
            { text: 'Account Settings', action: 'Help with account settings' },
            { text: 'Payment Issues', action: 'I have a payment question' },
            { text: 'Technical Problems', action: 'I\'m having technical issues' },
            { text: 'Send Feedback', action: 'How do I send feedback?' },
          ],
        };
      }
    }

    // Learning pathways and personalized recommendations
    if (lowerMessage.includes('learning path') || lowerMessage.includes('career path') ||
            lowerMessage.includes('recommended path') || lowerMessage.includes('suggest path')) {

      // If we have learning pathway data, provide personalized guidance
      if (learningPathways.length > 0) {
        const topPathways = learningPathways.slice(0, 3);
        const pathwaysText = topPathways.map(pathway => {
          return `- ${pathway.name}: ${pathway.description}\n  Courses: ${pathway.courses.length}, Duration: ${pathway.estimatedTimeToComplete || 'Varies'}`;
        }).join('\n\n');

        return {
          text: `Based on your interests and current courses, here are personalized learning pathways:\n\n${pathwaysText}\n\nThese pathways are designed to help you build complete skill sets progressively.`,
          sender: 'ai',
          timestamp: new Date().toLocaleTimeString(),
          suggestions: [
            ...topPathways.map(pathway => ({
              text: `Explore ${pathway.name}`,
              action: `Tell me more about ${pathway.name} pathway`,
            })),
            { text: 'All Pathways', action: 'Show all learning pathways' },
            { text: 'Custom Pathway', action: 'How do I create a custom pathway?' },
          ],
        };
      }
    }

    // Upcoming deadlines and important dates
    if (lowerMessage.includes('deadline') || lowerMessage.includes('due date') ||
            lowerMessage.includes('assignment due') || lowerMessage.includes('upcoming')) {

      if (upcomingDeadlines.length > 0) {
        // Sort deadlines by date
        const sortedDeadlines = [...upcomingDeadlines].sort((a, b) =>
          new Date(a.dueDate) - new Date(b.dueDate),
        );

        // Get the next 5 deadlines
        const nextDeadlines = sortedDeadlines.slice(0, 5);

        const deadlinesText = nextDeadlines.map(deadline => {
          const dueDate = new Date(deadline.dueDate).toLocaleDateString();
          const daysUntil = Math.ceil((new Date(deadline.dueDate) - new Date()) / (1000 * 60 * 60 * 24));
          const daysText = daysUntil === 0 ? 'Today' : daysUntil === 1 ? 'Tomorrow' : `In ${daysUntil} days`;

          return `- ${deadline.title}\n  Course: ${deadline.courseName}\n  Due: ${dueDate} (${daysText})\n  Type: ${deadline.type || 'Assignment'}`;
        }).join('\n\n');

        return {
          text: `Your upcoming deadlines:\n\n${deadlinesText}`,
          sender: 'ai',
          timestamp: new Date().toLocaleTimeString(),
          suggestions: [
            { text: 'All Deadlines', action: 'Show all deadlines' },
            { text: 'Calendar View', action: 'Navigate to study-calendar' },
            { text: 'This Week Only', action: 'Show deadlines this week' },
            { text: 'Set Reminders', action: 'How do I set deadline reminders?' },
          ],
        };
      }
    }

    // Course analytics and learning insights
    if (lowerMessage.includes('analytics') || lowerMessage.includes('learning stats') ||
            lowerMessage.includes('my stats') || lowerMessage.includes('study time') ||
            lowerMessage.includes('learning time')) {

      if (Object.keys(courseAnalytics).length > 0) {
        // Extract key analytics
        const totalTimeSpent = courseAnalytics.totalTimeSpent || '0 hours';
        const averageSessionDuration = courseAnalytics.avgSessionDuration || '0 minutes';
        const strongestSubject = courseAnalytics.strongestArea || 'Not enough data';
        const suggestedImprovement = courseAnalytics.improvementArea || 'Not enough data';
        const streakDays = courseAnalytics.currentStreak || 0;

        return {
          text: `Your Learning Analytics:\n\n1. Study Time: ${totalTimeSpent}\n2. Avg. Session: ${averageSessionDuration}\n3. Current Streak: ${streakDays} days\n4. Strongest Area: ${strongestSubject}\n5. Suggested Focus: ${suggestedImprovement}\n\nConsistent learning leads to better outcomes. Keep up your study routine!`,
          sender: 'ai',
          timestamp: new Date().toLocaleTimeString(),
          suggestions: [
            { text: 'Detailed Stats', action: 'Navigate to learning-statistics' },
            { text: 'Study Schedule', action: 'Help me create a study schedule' },
            { text: 'Improve Focus Area', action: `How to improve in ${suggestedImprovement}` },
            { text: 'Learning Tips', action: 'Give me learning tips' },
          ],
        };
      }
    }

    // Community engagement and forum discussions
    if (lowerMessage.includes('community') || lowerMessage.includes('forum') ||
            lowerMessage.includes('discussion') || lowerMessage.includes('study group')) {

      if (Object.keys(communityStats).length > 0) {
        // Extract community statistics
        const activeForums = communityStats.activeForums || [];
        const popularDiscussions = communityStats.popularDiscussions || [];
        const studyGroups = communityStats.studyGroups || [];

        // Create text about active forums
        let communityText = '';

        if (activeForums.length > 0) {
          communityText += 'Active Forums:\n' + activeForums.slice(0, 3).map(forum =>
            `- ${forum.name}: ${forum.participants} participants, ${forum.posts} posts`,
          ).join('\n') + '\n\n';
        }

        if (popularDiscussions.length > 0) {
          communityText += 'Popular Discussions:\n' + popularDiscussions.slice(0, 3).map(discussion =>
            `- ${discussion.title}\n  Replies: ${discussion.replies}, Views: ${discussion.views}`,
          ).join('\n') + '\n\n';
        }

        if (studyGroups.length > 0) {
          communityText += 'Study Groups:\n' + studyGroups.slice(0, 3).map(group =>
            `- ${group.name}\n  Members: ${group.members}, Focus: ${group.focus}`,
          ).join('\n');
        }

        if (communityText === '') {
          communityText = 'Community features are available but there\'s no activity data yet. Start participating to see statistics here!';
        }

        return {
          text: `Community Engagement:\n\n${communityText}`,
          sender: 'ai',
          timestamp: new Date().toLocaleTimeString(),
          suggestions: [
            { text: 'Join Discussion', action: 'Navigate to forums' },
            { text: 'Find Study Group', action: 'Navigate to study-groups' },
            { text: 'Create Study Group', action: 'How do I create a study group?' },
            { text: 'Community Guidelines', action: 'What are the community guidelines?' },
          ],
        };
      }
    }

    // Additional learning resources and materials
    if (lowerMessage.includes('resource') || lowerMessage.includes('material') ||
            lowerMessage.includes('extra learning') || lowerMessage.includes('supplemental')) {

      if (recommendedResources.length > 0) {
        const resourcesByType = recommendedResources.reduce((acc, resource) => {
          const type = resource.type || 'Other';
          if (!acc[type]) acc[type] = [];
          acc[type].push(resource);
          return acc;
        }, {});

        let resourcesText = '';

        for (const [type, resources] of Object.entries(resourcesByType)) {
          resourcesText += `${type}:\n` + resources.slice(0, 3).map(resource =>
            `- ${resource.title}\n  Course: ${resource.courseName || 'Multiple courses'}`,
          ).join('\n') + '\n\n';
        }

        return {
          text: `Recommended Learning Resources:\n\n${resourcesText}These supplemental materials can enhance your understanding of course concepts.`,
          sender: 'ai',
          timestamp: new Date().toLocaleTimeString(),
          suggestions: [
            { text: 'All Resources', action: 'Show all learning resources' },
            { text: 'Download Resources', action: 'How do I download resources?' },
            { text: 'External Materials', action: 'Show recommended external resources' },
            { text: 'Practice Exercises', action: 'Show practice exercises' },
          ],
        };
      }
    }

    // Course catalog search enhancement
    if ((lowerMessage.includes('find') || lowerMessage.includes('search')) &&
            (lowerMessage.includes('course') || lowerMessage.includes('class'))) {

      const searchTerm = extractSearchTerm(message);

      if (searchTerm && Object.keys(courseCatalogData).length > 0) {
        // Simulate searching through the course catalog
        const allCourses = courseCatalogData.courses || [];

        // Very simple search implementation - in a real app, this would be more sophisticated
        const matchingCourses = allCourses.filter(course =>
          course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    course.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    (course.tags && course.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))),
        );

        if (matchingCourses.length > 0) {
          // Group by category for better organization
          const coursesByCategory = matchingCourses.reduce((acc, course) => {
            const category = course.category || 'Uncategorized';
            if (!acc[category]) acc[category] = [];
            acc[category].push(course);
            return acc;
          }, {});

          let resultsText = '';

          for (const [category, courses] of Object.entries(coursesByCategory)) {
            resultsText += `${category}:\n` + courses.slice(0, 2).map(course =>
              `- ${course.title}\n  Instructor: ${course.instructor}\n  Level: ${course.level}\n  Rating: ${course.rating}/5 (${course.reviews} reviews)`,
            ).join('\n\n') + '\n\n';
          }

          const totalResults = matchingCourses.length;
          const shownResults = Object.values(coursesByCategory).reduce((sum, courses) => sum + Math.min(courses.length, 2), 0);

          if (shownResults < totalResults) {
            resultsText += `...and ${totalResults - shownResults} more courses match your search.`;
          }

          return {
            text: `Search results for "${searchTerm}":\n\n${resultsText}`,
            sender: 'ai',
            timestamp: new Date().toLocaleTimeString(),
            suggestions: [
              { text: 'All Results', action: `Show all ${searchTerm} courses` },
              { text: 'Beginner Courses', action: `Beginner ${searchTerm} courses` },
              { text: 'Advanced Courses', action: `Advanced ${searchTerm} courses` },
              { text: 'Best Rated', action: `Best rated ${searchTerm} courses` },
              { text: 'Refine Search', action: 'Refine my search' },
            ],
          };
        }
      }
    }

    // User preferences and personalization
    if (lowerMessage.includes('preference') || lowerMessage.includes('personalize') ||
            lowerMessage.includes('customize') || lowerMessage.includes('settings')) {

      if (Object.keys(userPreferences).length > 0) {
        const learningPrefs = userPreferences.learning || {};
        const notificationPrefs = userPreferences.notifications || {};
        const accessibilityPrefs = userPreferences.accessibility || {};
        const displayPrefs = userPreferences.display || {};

        return {
          text: `Your Preferences:\n\n1. Learning:\n   - Preferred time: ${learningPrefs.preferredTime || 'Not set'}\n   - Content level: ${learningPrefs.contentLevel || 'Not set'}\n   - Study session length: ${learningPrefs.sessionLength || 'Not set'}\n\n2. Notifications:\n   - Course updates: ${notificationPrefs.courseUpdates ? 'On' : 'Off'}\n   - Discussion replies: ${notificationPrefs.discussionReplies ? 'On' : 'Off'}\n   - Deadlines: ${notificationPrefs.deadlines ? 'On' : 'Off'}\n\n3. Display & Accessibility:\n   - Theme: ${displayPrefs.theme || 'Default'}\n   - Font size: ${accessibilityPrefs.fontSize || 'Default'}\n   - Caption preferences: ${accessibilityPrefs.captionPrefs || 'Default'}`,
          sender: 'ai',
          timestamp: new Date().toLocaleTimeString(),
          suggestions: [
            { text: 'Learning Preferences', action: 'Navigate to learning-preferences' },
            { text: 'Notification Settings', action: 'Navigate to notification-settings' },
            { text: 'Accessibility Options', action: 'Navigate to accessibility-settings' },
            { text: 'Display Settings', action: 'Navigate to display-settings' },
            { text: 'Reset Preferences', action: 'How do I reset my preferences?' },
          ],
        };
      }
    }

    // Learning styles and educational methodologies
    if (lowerMessage.includes('learning style') || lowerMessage.includes('study method') ||
            lowerMessage.includes('learning technique') || lowerMessage.includes('study style')) {

      return {
        text: 'Learning Styles & Methodologies:\n\n1. Visual Learning:\n   - Video lectures and demonstrations\n   - Infographics and diagrams\n   - Mind mapping and concept visualization\n   - Color-coding notes\n\n2. Auditory Learning:\n   - Recorded lectures and discussions\n   - Reading material aloud\n   - Discussing concepts with others\n   - Using mnemonic devices\n\n3. Reading/Writing:\n   - Taking detailed notes\n   - Summarizing content in writing\n   - Creating flashcards\n   - Reading additional materials\n\n4. Kinesthetic Learning:\n   - Hands-on projects and exercises\n   - Interactive simulations\n   - Code practice and labs\n   - Teaching concepts to others\n\n5. Effective Study Techniques:\n   - Spaced repetition for memorization\n   - Pomodoro technique (25 min work, 5 min break)\n   - Active recall vs passive reviewing\n   - Interleaving (mixing different topics)\n\nOur platform supports all learning styles with varied content formats.',
        sender: 'ai',
        timestamp: new Date().toLocaleTimeString(),
        type: 'guidance',
        suggestions: [
          { text: 'Find My Style', action: 'How do I identify my learning style?' },
          { text: 'Visual Learning', action: 'Tips for visual learners' },
          { text: 'Auditory Learning', action: 'Tips for auditory learners' },
          { text: 'Reading/Writing', action: 'Tips for reading/writing learners' },
          { text: 'Kinesthetic Learning', action: 'Tips for kinesthetic learners' },
        ],
      };
    }

    // Certification and exam preparation 
    if (lowerMessage.includes('certification') || lowerMessage.includes('exam prep') ||
            lowerMessage.includes('prepare for exam') || lowerMessage.includes('test preparation')) {

      return {
        text: 'Certification & Exam Preparation:\n\n1. Preparation Strategies:\n   - Understand exam format and requirements\n   - Create a study schedule with milestones\n   - Take practice tests under timed conditions\n   - Focus on weak areas identified in practice tests\n\n2. Platform Resources:\n   - Practice exams with solutions\n   - Exam-specific courses and prep materials\n   - Flashcards and quick review guides\n   - Instructor Q&A sessions for exam topics\n\n3. Time Management:\n   - Allocate study time based on topic weight\n   - Create a countdown schedule to exam day\n   - Build in review days before the exam\n   - Schedule breaks to prevent burnout\n\n4. Mind & Body Preparation:\n   - Ensure proper sleep before exams\n   - Light review on exam day, not cramming\n   - Hydration and proper nutrition\n   - Stress management techniques\n\nMany of our courses include certification preparation tracks specifically designed for official exams.',
        sender: 'ai',
        timestamp: new Date().toLocaleTimeString(),
        type: 'guidance',
        suggestions: [
          { text: 'Certification Courses', action: 'Show certification prep courses' },
          { text: 'Practice Exams', action: 'Where can I find practice exams?' },
          { text: 'Study Schedule', action: 'Help me create a certification study plan' },
          { text: 'Test Anxiety', action: 'Tips for managing test anxiety' },
          { text: 'Mock Interview', action: 'Are there mock interviews for certifications?' },
        ],
      };
    }

    // Career guidance and professional development
    if (lowerMessage.includes('career') || lowerMessage.includes('job') ||
            lowerMessage.includes('professional') || lowerMessage.includes('resume') ||
            lowerMessage.includes('interview')) {

      return {
        text: 'Career Development Resources:\n\n1. Career Pathway Guidance:\n   - Industry-specific learning paths\n   - Skills gap analysis tools\n   - Career transition roadmaps\n   - Salary and job market information\n\n2. Resume & Portfolio Building:\n   - Certificate showcase features\n   - Portfolio project suggestions\n   - Resume building workshops\n   - Skills verification badges\n\n3. Interview Preparation:\n   - Technical interview practice\n   - Common interview question banks\n   - Mock interview scenarios\n   - Feedback and improvement tools\n\n4. Professional Networking:\n   - Industry expert webinars\n   - Networking events calendar\n   - Mentor connection opportunities\n   - Professional community forums\n\nOur platform partners with industry leaders to ensure our content aligns with current job market demands.',
        sender: 'ai',
        timestamp: new Date().toLocaleTimeString(),
        suggestions: [
          { text: 'Career Paths', action: 'Show career paths in my field' },
          { text: 'Resume Help', action: 'How to showcase courses on resume' },
          { text: 'Interview Prep', action: 'Interview preparation resources' },
          { text: 'Networking', action: 'Professional networking opportunities' },
          { text: 'Career Coaching', action: 'Is career coaching available?' },
        ],
      };
    }

    // Subject-specific study strategies (programming, business, languages, etc.)
    if (lowerMessage.includes('how to learn') || lowerMessage.includes('study strategy for')) {

      // Check for programming-related queries
      if (lowerMessage.includes('program') || lowerMessage.includes('coding') ||
                lowerMessage.includes('development') || lowerMessage.includes('software')) {

        return {
          text: 'Programming Study Strategies:\n\n1. Project-Based Learning:\n   - Build real projects from day one\n   - Start small and incrementally add features\n   - Use version control (e.g., Git) for all projects\n   - Document your code and learning process\n\n2. Coding Practice:\n   - Daily coding challenges (LeetCode, HackerRank)\n   - Pair programming with study partners\n   - Code reviews to improve quality\n   - Refactoring exercises for existing code\n\n3. Resource Utilization:\n   - Official documentation as primary reference\n   - Interactive coding environments\n   - Open source project analysis\n   - Developer communities (Stack Overflow, GitHub)\n\n4. Debugging Skills:\n   - Rubber duck debugging technique\n   - Using debugging tools effectively\n   - Writing test cases before coding\n   - Problem decomposition methods\n\nOur programming courses emphasize hands-on practice with immediate feedback and real-world applications.',
          sender: 'ai',
          timestamp: new Date().toLocaleTimeString(),
          type: 'guidance',
          suggestions: [
            { text: 'Coding Projects', action: 'Recommend beginner coding projects' },
            { text: 'Practice Resources', action: 'Where can I practice coding?' },
            { text: 'Debugging Tips', action: 'Advanced debugging techniques' },
            { text: 'Code Review', action: 'How to get code reviews' },
            { text: 'Programming Roadmap', action: 'Show programming learning roadmap' },
          ],
        };
      }

      // Check for business-related queries
      if (lowerMessage.includes('business') || lowerMessage.includes('management') ||
                lowerMessage.includes('marketing') || lowerMessage.includes('finance')) {

        return {
          text: 'Business Study Strategies:\n\n1. Case Study Analysis:\n   - Breakdown real business scenarios\n   - Apply theoretical frameworks to cases\n   - Practice decision-making rationales\n   - Group discussions of case outcomes\n\n2. Market Simulation:\n   - Business simulation games\n   - Mock investment portfolios\n   - Marketing campaign planning\n   - Financial modeling exercises\n\n3. Current Affairs Integration:\n   - Connect theories to current business news\n   - Industry trend analysis exercises\n   - Company performance evaluations\n   - Economic indicator tracking\n\n4. Networking & Perspective:\n   - Expert interviews and panels\n   - Industry conference summaries\n   - Cross-functional business perspectives\n   - Global business practice comparison\n\nOur business courses incorporate real-world scenarios and industry benchmarks to ensure practical relevance.',
          sender: 'ai',
          timestamp: new Date().toLocaleTimeString(),
          type: 'guidance',
          suggestions: [
            { text: 'Case Studies', action: 'Where do I find business case studies?' },
            { text: 'Simulations', action: 'Business simulation resources' },
            { text: 'Analysis Tools', action: 'Business analysis frameworks' },
            { text: 'Current Trends', action: 'Latest business trends courses' },
            { text: 'Business Roadmap', action: 'Show business learning roadmap' },
          ],
        };
      }

      // Check for language learning queries
      if (lowerMessage.includes('language') || lowerMessage.includes('spanish') ||
                lowerMessage.includes('french') || lowerMessage.includes('english') ||
                lowerMessage.includes('japanese') || lowerMessage.includes('chinese')) {

        return {
          text: 'Language Learning Strategies:\n\n1. Immersion Techniques:\n   - Daily listening practice with native content\n   - Language switching periods during the day\n   - Media consumption in target language\n   - Label household items in the new language\n\n2. Conversation Practice:\n   - Language exchange partners\n   - Structured conversation scenarios\n   - Voice recording and self-assessment\n   - Mimicking native speaker patterns\n\n3. Vocabulary Building:\n   - Spaced repetition flashcards\n   - Thematic vocabulary clusters\n   - Etymology and word family studies\n   - Context-based word acquisition\n\n4. Grammar Integration:\n   - Pattern recognition before rules\n   - Sentence mining from authentic materials\n   - Error correction journals\n   - Grammar in context vs. isolated rules\n\nOur language courses balance structured learning with authentic communication practice for optimal results.',
          sender: 'ai',
          timestamp: new Date().toLocaleTimeString(),
          type: 'guidance',
          suggestions: [
            { text: 'Immersion Resources', action: 'Language immersion techniques' },
            { text: 'Conversation Practice', action: 'Where to practice speaking' },
            { text: 'Vocabulary Apps', action: 'Best vocabulary building tools' },
            { text: 'Grammar Methods', action: 'Effective grammar learning methods' },
            { text: 'Language Roadmap', action: 'Show language learning roadmap' },
          ],
        };
      }

      // Default learning strategies for other subjects
      return {
        text: 'General Learning Strategies:\n\n1. Active Learning:\n   - Convert passive content into active engagement\n   - Create questions from learning materials\n   - Teach concepts to others (even imaginary students)\n   - Apply concepts to real-world problems\n\n2. Organization Methods:\n   - Mind mapping for concept relationships\n   - Cornell note-taking system\n   - Concept hierarchies and knowledge trees\n   - Spacing study sessions for retention\n\n3. Memory Techniques:\n   - Spaced repetition for long-term memory\n   - Mnemonic devices for complex information\n   - Memory palace/method of loci for sequences\n   - Chunking information into manageable groups\n\n4. Practical Application:\n   - Project-based learning exercises\n   - Real-world application scenarios\n   - Problem-solving with learned concepts\n   - Peer teaching and discussion\n\nThe most effective learning happens when you actively engage with material rather than passively consuming it.',
        sender: 'ai',
        timestamp: new Date().toLocaleTimeString(),
        type: 'guidance',
        suggestions: [
          { text: 'Active Learning', action: 'More active learning techniques' },
          { text: 'Note-Taking', action: 'Effective note-taking methods' },
          { text: 'Memory Techniques', action: 'Advanced memory techniques' },
          { text: 'Study Schedule', action: 'Help me create a study schedule' },
          { text: 'Subject-Specific', action: 'Learning techniques for my specific subject' },
        ],
      };
    }

    // Project collaboration and group work
    if (lowerMessage.includes('group project') || lowerMessage.includes('collaboration') ||
            lowerMessage.includes('team work') || lowerMessage.includes('project team')) {

      return {
        text: 'Collaboration Tools & Techniques:\n\n1. Platform Collaboration Features:\n   - Group spaces for project teams\n   - Shared document repositories\n   - Task assignment and tracking\n   - Collaborative calendars and milestones\n\n2. Communication Channels:\n   - Discussion boards for asynchronous communication\n   - Video conferencing integration\n   - Comment threads on specific materials\n   - Direct messaging with team members\n\n3. Project Management:\n   - Task breakdown templates\n   - Progress tracking dashboards\n   - Role assignment tools\n   - Deadline management features\n\n4. Collaborative Learning:\n   - Peer review workflows\n   - Group assessment options\n   - Contribution tracking tools\n   - Feedback collection mechanisms\n\nEffective collaboration requires clear communication, well-defined roles, and appropriate tools for your specific project needs.',
        sender: 'ai',
        timestamp: new Date().toLocaleTimeString(),
        suggestions: [
          { text: 'Create Group', action: 'How do I create a project group?' },
          { text: 'Collaboration Tools', action: 'What collaboration tools are available?' },
          { text: 'Project Templates', action: 'Group project templates' },
          { text: 'Peer Review', action: 'Set up peer review process' },
          { text: 'Manage Deadlines', action: 'How to manage group deadlines' },
        ],
      };
    }

    // Feedback form guidance - differentiate between user and instructor feedback
    if (lowerMessage.includes('feedback') || lowerMessage.includes('send feedback') ||
            lowerMessage.includes('submit feedback') || lowerMessage.includes('feedback form') ||
            lowerMessage.includes('provide feedback') || lowerMessage.includes('give feedback')) {

      // Check if user is an instructor
      const isInstructor = userRoles.includes('instructor');

      if (isInstructor) {
        return {
          text: 'How to Send Feedback (Instructor):\n\nAs an instructor, you can submit feedback about the platform:\n\n✅ Access Feedback Form:\n   1. Go to your Instructor Dashboard\n   2. Click on your profile icon (in the top bar)\n   3. Select "Feedback" from the dropdown menu\n   4. Fill out and submit the feedback form\n\n📝 What to Include:\n   - Course management features feedback\n   - Student engagement tools\n   - Platform functionality issues\n   - Teaching experience improvements\n   - Technical problems\n   - New feature suggestions\n\n⏱️ Response Time:\n   - Admin team reviews all instructor feedback\n   - Response within 48-72 hours\n   - High-priority issues are escalated immediately\n\n📧 Note: For urgent technical support, contact support@ulsaapp.com\n\nYour feedback helps us improve the teaching experience on UlsaApp!',
          sender: 'ai',
          timestamp: new Date().toLocaleTimeString(),
          type: 'guidance',
          suggestions: [
            { text: 'Instructor Dashboard', action: 'Navigate to instructor-dashboard' },
            { text: 'Teaching Resources', action: 'What teaching resources are available?' },
            { text: 'Help Center', action: 'Navigate to help-center' },
            { text: 'Platform Updates', action: 'What are the latest platform updates?' },
          ],
        };
      } else {
        // Regular user feedback
        return {
          text: 'How to Send Feedback:\n\nWe value your feedback! Here are the two ways to access the feedback form:\n\n✅ Method 1 - Profile Menu:\n   1. Click on your profile icon (top right corner)\n   2. Select "Feedback" from the dropdown menu\n   3. Fill out and submit the feedback form\n\n✅ Method 2 - Footer:\n   1. Scroll to the bottom of the home page\n   2. Click on the "Feedback" link in the footer\n   3. Fill out and submit the feedback form\n\n📝 What to Include:\n   - Course quality and content feedback\n   - User experience suggestions\n   - Platform features you\'d like to see\n   - Any issues or bugs you\'ve encountered\n   - Ideas for improvement\n\n⏱️ Response Time:\n   - Your feedback will be reviewed by our admin team\n   - Expect a response within 24-48 hours\n   - High-priority issues are addressed immediately\n\n📧 Note: The feedback form is specifically for platform feedback. For general support queries, please use support@ulsaapp.com\n\nYour feedback helps us make UlsaApp better for everyone!',
          sender: 'ai',
          timestamp: new Date().toLocaleTimeString(),
          type: 'guidance',
          suggestions: [
            { text: 'Go to Home Page', action: 'Navigate to /' },
            { text: 'My Profile', action: 'Navigate to profile' },
            { text: 'Help Center', action: 'Navigate to help-center' },
            { text: 'Other Support', action: 'I need other support' },
          ],
        };
      }
    }

    // Add admin contact handling to processMessage function
    if (lowerMessage.includes('admin contact') ||
            lowerMessage.includes('contact admin') ||
            lowerMessage.includes('speak to administrator') ||
            lowerMessage.includes('talk to admin') ||
            lowerMessage.includes('contact administrator') ||
            lowerMessage.includes('contact support')) {

      const adminContactSuggestions = [
        { text: 'Submit Ticket', action: 'Navigate to /contact-admin' },
        { text: 'Live Chat', action: 'Navigate to live-chat' },
        { text: 'Book Meeting', action: 'Navigate to admin-calendar' },
        { text: 'Admin FAQ', action: 'Show administrator FAQ' },
        { text: 'Report Issue', action: 'How do I report a serious platform issue?' },
      ];

      return {
        text: 'Contact Support Information:\n\n📧 Support Email: support@ulsaapp.com\n   - For general support queries\n   - Technical issues and troubleshooting\n   - Account-related problems\n   - Urgent platform issues\n\n⚠️ Note: For platform feedback and suggestions, please use the Feedback Form (available in your profile menu or footer), NOT support email.\n\n📞 Support Hours:\n   - Monday-Friday, 9:00 AM - 5:00 PM EST\n   - Average response time: 24-48 hours\n   - Urgent issues marked "URGENT" are prioritized\n\n✅ Before Contacting Support:\n   1. Check our FAQ section first\n   2. Try the AI Assistant for common issues\n   3. Ensure your issue is platform-related\n   4. Include your user ID and specific details\n\n💡 Quick Tip: Use the Feedback Form for suggestions and improvements!',
        sender: 'ai',
        timestamp: new Date().toLocaleTimeString(),
        suggestions: filterSuggestions(adminContactSuggestions),
      };
    }

    // Default response with complete service overview
    return {
      text: 'Welcome to the Learning Management System! I\'m your AI Assistant, ready to help with:\n\n1. Course Information:\n   - Finding courses\n   - Course content and structure\n   - Prerequisites and requirements\n   - Certificates and credentials\n\n2. Learning Support:\n   - Study techniques and tips\n   - Progress tracking\n   - Assignment help\n   - Technical troubleshooting\n\n3. Account & Purchases:\n   - Account settings\n   - Payment options\n   - Enrollments\n   - Refunds and policies\n\nHow can I assist you today?',
      sender: 'ai',
      timestamp: new Date().toLocaleTimeString(),
      suggestions: [
        { text: 'Find Courses', action: 'Show me popular courses' },
        { text: 'My Learning', action: 'Show my courses' },
        { text: 'Account Help', action: 'Help with my account' },
        { text: 'Payment Help', action: 'Help with payments' },
        { text: 'Technical Support', action: 'I need technical help' },
      ],
    };
  };

  // Add this useEffect after other useEffects
  useEffect(() => {
    // Set learning tips based on user's enrolled courses
    if (userCourses && userCourses.length > 0) {
      const tips = [
        'Did you know you can download course videos for offline viewing in our mobile app?',
        'Setting specific learning goals can increase your course completion rate by 70%.',
        'Try the Pomodoro technique: 25 minutes of focused study, then a 5-minute break.',
        'Taking notes while watching videos improves retention by up to 50%.',
        'Most successful learners complete at least 15-30 minutes of learning daily.',
        'Need administrative help? Use the \'Contact Admin\' quick access button in the assistant.',
        'You can request special accommodations by contacting an administrator through the admin portal.',
        'Administrators can help with course extension requests if you need more time to complete a course.',
        'Having technical issues? Our mobile app offers an offline mode while you wait for resolution.',
        'Set your learning preferences in your profile to get more personalized course recommendations.',
      ];
      setLearningTips(tips);
    }

    // Track current location for context-aware responses
    setCurrentLocation(window.location.pathname);
  }, [userCourses]);

  // Update the quick access panel to include admin contact
  const renderQuickAccessPanel = () => {
    if (!quickAccessVisible) return null;

    return (
      <div className="ai-assistant-quick-access-panel">
        <div className="ai-assistant-quick-access-category">
          <div className="ai-assistant-category-title">
            <BiBookOpen /> Learning
          </div>
          <div className="ai-assistant-quick-access-buttons">
            <button onClick={() => handleSuggestionClick('Show my courses')}>My Courses</button>
            <button onClick={() => handleSuggestionClick('How do I track my progress?')}>Progress</button>
            <button onClick={() => handleSuggestionClick('Give me study tips')}>Study Tips</button>
          </div>
        </div>
        <div className="ai-assistant-quick-access-category">
          <div className="ai-assistant-category-title">
            <BiCreditCardFront /> Purchases
          </div>
          <div className="ai-assistant-quick-access-buttons">
            <button onClick={() => handleSuggestionClick('How do I enroll in a course?')}>Enrollment</button>
            <button onClick={() => handleSuggestionClick('What payment methods are accepted?')}>Payment</button>
            <button onClick={() => handleSuggestionClick('What is the refund policy?')}>Refunds</button>
          </div>
        </div>
        <div className="ai-assistant-quick-access-category">
          <div className="ai-assistant-category-title">
            <BiUserCircle /> Account
          </div>
          <div className="ai-assistant-quick-access-buttons">
            <button onClick={() => handleSuggestionClick('How do I edit my profile?')}>Profile</button>
            <button onClick={() => handleSuggestionClick('How do I change my password?')}>Password</button>
            <button onClick={() => handleSuggestionClick('Privacy settings')}>Privacy</button>
          </div>
        </div>
        <div className="ai-assistant-quick-access-category">
          <div className="ai-assistant-category-title">
            <BiHelpCircle /> Support
          </div>
          <div className="ai-assistant-quick-access-buttons">
            <button onClick={() => handleSuggestionClick('Technical support')}>Technical</button>
            <button onClick={() => handleSuggestionClick('How do I contact support?')}>Contact</button>
            <button onClick={() => handleSuggestionClick('Mobile app help')}>Mobile App</button>
            <button onClick={() => handleSuggestionClick('Contact admin')}>Admin Help</button>
          </div>
        </div>
      </div>
    );
  };

  // Add daily tips feature
  const renderDailyTip = () => {
    if (!learningTips || learningTips.length === 0) return null;

    // Get random tip
    const randomIndex = Math.floor(Math.random() * learningTips.length);
    const tip = learningTips[randomIndex];

    return (
      <div className="ai-assistant-daily-tip">
        <div className="ai-assistant-tip-header">💡 Learning Tip</div>
        <div className="ai-assistant-tip-content">{tip}</div>
      </div>
    );
  };

  // Add this function to check if pages exist in the system
  const checkPageExists = (pagePath) => {
    // Define all available page paths in the system
    const availablePages = [
      '/',
      '/learning',
      '/courses',
      '/help',
      '/progress',
      '/learning-statistics',
      '/my-certificates',
      '/cart',
      '/purchases',
      '/payment-methods',
      '/promotions',
      '/edit-profile',
      '/account-settings',
      '/learning-preferences',
      '/notification-settings',
      '/privacy-settings',
      '/help-center',
      '/technical-support',
      '/contact-admin',
      '/faqs',
      '/system-status',
      '/forums',
      '/study-groups',
      '/community',
      '/classmates',
      '/events',
      '/app-store',
      '/play-store',
      '/study-calendar',
      '/resource-library',
      '/contact-support',
      '/live-chat',
      '/admin-calendar',
    ];

    // Check if the path exists (exact match)
    return availablePages.includes(pagePath);
  };

  // Add this function to parse navigation actions
  const parseNavigationAction = (action) => {
    if (!action) return null;

    // Handle direct navigation commands
    if (action.startsWith('Navigate to ')) {
      const path = action.replace('Navigate to ', '');
      return '/' + path.toLowerCase().replace(/\s+/g, '-');
    }

    // Map common action phrases to paths
    const actionMap = {
      'Show my courses': '/learning',
      'Navigate to learning': '/learning',
      'Show popular courses': '/courses',
      'Navigate to courses': '/courses',
      'Navigate to contact-admin': '/contact-admin',
      'Navigate to help': '/help',
      'Navigate to admin-contact': '/contact-admin',
      'Navigate to live-chat': '/live-chat',
      'Navigate to admin-calendar': '/admin-calendar',
      'Navigate to edit-profile': '/edit-profile',
      'Navigate to account-settings': '/account-settings',
      'Navigate to certificates': '/my-certificates',
      'Navigate to learning-statistics': '/learning-statistics',
      'Navigate to study-calendar': '/study-calendar',
      'Navigate to app-store': '/app-store',
      'Navigate to play-store': '/play-store',
      'Navigate to help-center': '/help-center',
      'Navigate to learning-preferences': '/learning-preferences',
      'Show all learning pathways': '/learning-paths',
      'Navigate to learning-paths': '/learning-paths',
      'Show learning paths': '/learning-paths',
      'All Pathways': '/learning-paths',
      'Explore learning paths': '/learning-paths',
    };

    // Check if the action is in our map
    if (actionMap[action]) {
      return actionMap[action];
    }

    // Additional parsing for actions that mention paths
    if (action.includes('path') || action.includes('paths')) {
      if (action.includes('learning') || action.includes('study')) {
        return '/learning-paths';
      }
    }

    return null;
  };

  // Enhance the filterSuggestions function with better validation
  const filterSuggestions = (suggestions) => {
    if (!suggestions || !Array.isArray(suggestions)) return [];

    // More aggressive filtering to ensure no unavailable page suggestions appear
    return suggestions.filter(suggestion => {
      // Skip invalid suggestion objects
      if (!suggestion || typeof suggestion !== 'object') return false;
      if (!suggestion.action || typeof suggestion.action !== 'string') return false;

      // Parse the potential navigation path
      const path = parseNavigationAction(suggestion.action);

      // If it's not a navigation action (no path), keep the suggestion
      if (!path) return true;

      // If it's a navigation action, only keep it if the path exists
      return checkPageExists(path);
    });
  };

  // Add this function to handle suggestion clicks
  const handleSuggestionClick = (action) => {
    // Close the quick access panel when any button is clicked
    setQuickAccessVisible(false);
        
    // Check if this is a navigation action
    const path = parseNavigationAction(action);

    if (path && checkPageExists(path)) {
      // Add navigation message
      setMessages(prev => [...prev, {
        text: `Navigating to ${path.substring(1)}...`,
        sender: 'ai',
        timestamp: new Date().toLocaleTimeString(),
        type: 'navigation',
      }]);

      // Navigate to the page
      navigate(path);
    } else {
      // Handle as a regular chat input
      setInputMessage(action);
      handleSendMessage({ preventDefault: () => { } });
    }
  };

  // Add this useEffect to fetch comprehensive data from across the LMS
  useEffect(() => {
    if (!token || !isOpen) return;

    const fetchComprehensiveLMSData = async () => {
      try {
        // Fetch course catalog data for better recommendations
        if (api.getCourseCatalog) {
          const catalogResponse = await axios.get(api.getCourseCatalog, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setCourseCatalogData(catalogResponse.data || {});
        }

        // Fetch user learning analytics for personalized insights
        if (api.getUserAnalytics) {
          const analyticsResponse = await axios.get(api.getUserAnalytics, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setCourseAnalytics(analyticsResponse.data || {});
        }

        // Fetch recommended learning pathways based on user's courses and interests
        if (api.getLearningPathways) {
          const pathwaysResponse = await axios.get(api.getLearningPathways, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setLearningPathways(pathwaysResponse.data?.pathways || []);
        }

        // Fetch upcoming deadlines and important dates
        if (api.getUpcomingDeadlines) {
          const deadlinesResponse = await axios.get(api.getUpcomingDeadlines, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setUpcomingDeadlines(deadlinesResponse.data?.deadlines || []);
        }

        // Fetch community engagement statistics
        if (api.getCommunityStats) {
          const communityResponse = await axios.get(api.getCommunityStats, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setCommunityStats(communityResponse.data || {});
        }

        // Fetch user preferences for personalized assistance
        if (api.getUserPreferences) {
          const preferencesResponse = await axios.get(api.getUserPreferences, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setUserPreferences(preferencesResponse.data || {});
        }

        // Fetch recommended learning resources based on courses
        if (userCourses.length > 0 && api.getRecommendedResources) {
          const courseIds = userCourses.map(course => course._id);
          const resourcesResponse = await axios.post(api.getRecommendedResources, {
            courseIds: courseIds,
          }, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setRecommendedResources(resourcesResponse.data?.resources || []);
        }

      } catch (error) {
        // console.error('Error fetching comprehensive UlsaApp data:', error);
      }
    };

    fetchComprehensiveLMSData();
  }, [token, isOpen, userCourses]);

  // First, let's create a safety check helper for API endpoints
  const _isApiEndpointAvailable = (endpoint) => {
    return endpoint !== undefined && endpoint !== null && typeof endpoint === 'string';
  };

  // Add this function to check if we have sufficient data to provide a proper response
  const hasEnoughDataToRespond = (dataSource, minimumRequiredLength = 0) => {
    // Check for arrays
    if (Array.isArray(dataSource)) {
      return dataSource.length > minimumRequiredLength;
    }

    // Check for objects
    if (dataSource && typeof dataSource === 'object') {
      return Object.keys(dataSource).length > minimumRequiredLength;
    }

    // Check for strings and other primitives
    return !!dataSource;
  };

  // Fix the broken processMessageWithValidation function
  const _processMessageWithValidation = async (message) => {
    const lowerMessage = message.toLowerCase();

    try {
      // Handle learning paths specifically
      if (lowerMessage.includes('learning path') ||
                lowerMessage.includes('learning pathway') ||
                lowerMessage.includes('study path')) {

        // Check if we have the replacement message stored
        const storedReplacement = sessionStorage.getItem('learningPathwaysReplacement');
        if (storedReplacement) {
          try {
            const replacement = JSON.parse(storedReplacement);
            // Apply filtering to ensure no broken links
            if (replacement.suggestions) {
              replacement.suggestions = filterSuggestions(replacement.suggestions);
            }
            return replacement;
          } catch (e) {
            // console.error('Error parsing stored replacement:', e);
          }
        }

        // Fallback if no stored replacement
        return {
          text: 'We\'re developing our learning pathways feature. In the meantime, I can help you find specific courses or topics you\'re interested in.',
          sender: 'ai',
          timestamp: new Date().toLocaleTimeString(),
          suggestions: filterSuggestions([
            { text: 'Popular Courses', action: 'Show popular courses' },
            { text: 'Course Categories', action: 'Show course categories' },
            { text: 'Help Me Choose', action: 'Help me choose a course' },
          ]),
        };
      }

      // Data availability checks for specific query types
      if ((lowerMessage.includes('analytics') || lowerMessage.includes('learning stats')) &&
                !hasEnoughDataToRespond(courseAnalytics)) {
        return {
          text: 'I\'d like to show you your learning analytics, but we don\'t have enough data yet. Continue using the platform and check back later for personalized insights!',
          sender: 'ai',
          timestamp: new Date().toLocaleTimeString(),
          suggestions: filterSuggestions([
            { text: 'My Courses', action: 'Show my courses' },
            { text: 'Start Learning', action: 'Navigate to learning' },
            { text: 'Analytics FAQ', action: 'How are analytics calculated?' },
          ]),
        };
      }

      if (lowerMessage.includes('resource') && !hasEnoughDataToRespond(recommendedResources)) {
        return {
          text: 'I\'d like to recommend learning resources, but I don\'t have enough information about your interests yet. As you explore more courses, I\'ll be able to suggest relevant materials.',
          sender: 'ai',
          timestamp: new Date().toLocaleTimeString(),
          suggestions: filterSuggestions([
            { text: 'General Resources', action: 'Show popular learning resources' },
            { text: 'Resource Library', action: 'Navigate to resource-library' },
            { text: 'Help Center', action: 'Navigate to help-center' },
          ]),
        };
      }

      if ((lowerMessage.includes('deadline') || lowerMessage.includes('due date')) &&
                !hasEnoughDataToRespond(upcomingDeadlines)) {
        return {
          text: 'I don\'t see any upcoming deadlines in your schedule. This could be because you haven\'t enrolled in courses with deadlines, or your courses don\'t have any deadlines approaching soon.',
          sender: 'ai',
          timestamp: new Date().toLocaleTimeString(),
          suggestions: filterSuggestions([
            { text: 'My Calendar', action: 'Navigate to study-calendar' },
            { text: 'Set Reminders', action: 'How do I set reminders?' },
            { text: 'Course Schedule', action: 'Show my course schedule' },
          ]),
        };
      }

      // If we have sufficient data or the query is for something else, 
      // process normally with the original function
      const response = await processMessage(message);

      // Filter suggestions in all responses to ensure we never show unavailable pages
      if (response && response.suggestions) {
        response.suggestions = filterSuggestions(response.suggestions);
      }

      return response;
    }
    catch (error) {
      // console.error('Error processing message with validation:', error);

      // Return a graceful error message
      return {
        text: 'I\'m having trouble processing your request at the moment. This might be due to a temporary issue or missing data. Could you try asking something else or try again later?',
        sender: 'ai',
        timestamp: new Date().toLocaleTimeString(),
        type: 'warning',
        suggestions: filterSuggestions([
          { text: 'Help Topics', action: 'What can you help with?' },
          { text: 'Course Help', action: 'Help with my courses' },
          { text: 'Technical Support', action: 'I need technical support' },
        ]),
      };
    }
  };

  // Add the getRelatedPages function back
  const getRelatedPages = (conversation) => {
    // Default related pages for any conversation
    const defaultRelatedPages = [
      { name: 'Home', path: '/' },
      { name: 'Learning Dashboard', path: '/learning' },
      { name: 'Course Catalog', path: '/courses' },
      { name: 'Help Center', path: '/help' },
    ];

    // If no conversation exists, return defaults
    if (!conversation || conversation.length === 0) {
      return defaultRelatedPages;
    }

    // Get the last few messages to determine context
    const recentMessages = conversation.slice(-3);
    const recentText = recentMessages.map(msg => msg.text.toLowerCase()).join(' ');

    // Define page categories with related pages
    const pageCategories = [
      {
        keywords: ['course', 'learning', 'lesson', 'module', 'study', 'progress'],
        pages: [
          { name: 'My Courses', path: '/learning' },
          { name: 'Course Progress', path: '/progress' },
          { name: 'Learning Statistics', path: '/learning-statistics' },
          { name: 'Certificates', path: '/my-certificates' },
        ],
      },
      {
        keywords: ['payment', 'billing', 'purchase', 'cart', 'buy', 'checkout', 'enroll'],
        pages: [
          { name: 'Shopping Cart', path: '/cart' },
          { name: 'My Purchases', path: '/purchases' },
          { name: 'Payment Methods', path: '/payment-methods' },
          { name: 'Course Catalog', path: '/courses' },
          { name: 'Promotions', path: '/promotions' },
        ],
      },
      {
        keywords: ['account', 'profile', 'settings', 'password', 'email', 'preferences'],
        pages: [
          { name: 'Edit Profile', path: '/edit-profile' },
          { name: 'Account Settings', path: '/account-settings' },
          { name: 'Learning Preferences', path: '/learning-preferences' },
          { name: 'Notification Settings', path: '/notification-settings' },
          { name: 'Privacy Settings', path: '/privacy-settings' },
        ],
      },
      {
        keywords: ['support', 'help', 'technical', 'issue', 'problem', 'contact', 'admin'],
        pages: [
          { name: 'Help Center', path: '/help' },
          { name: 'Technical Support', path: '/technical-support' },
          { name: 'Contact Admin', path: '/contact-admin' },
          { name: 'FAQs', path: '/faqs' },
          { name: 'System Status', path: '/system-status' },
        ],
      },
      {
        keywords: ['community', 'forum', 'discussion', 'group', 'social', 'collaboration'],
        pages: [
          { name: 'Discussion Forums', path: '/forums' },
          { name: 'Study Groups', path: '/study-groups' },
          { name: 'Community Hub', path: '/community' },
          { name: 'Find Classmates', path: '/classmates' },
          { name: 'Events Calendar', path: '/events' },
        ],
      },
    ];

    // Score each category based on keyword matches
    const categoryScores = pageCategories.map(category => {
      let score = 0;
      category.keywords.forEach(keyword => {
        // Count occurrences of each keyword
        const regex = new RegExp(keyword, 'gi');
        const matches = recentText.match(regex);
        score += matches ? matches.length : 0;
      });
      return {
        score,
        pages: category.pages,
      };
    });

    // Sort categories by score (descending)
    categoryScores.sort((a, b) => b.score - a.score);

    // If no strong matches, return default
    if (categoryScores[0].score === 0) {
      return defaultRelatedPages;
    }

    // Return pages from the highest scoring category
    return categoryScores[0].pages;
  };

  const showRelatedPages = () => {
    const relatedPages = getRelatedPages(messages);

    // Create a message with related page navigation options
    const relatedPagesMessage = {
      text: 'Here are some related pages that might be helpful:',
      sender: 'ai',
      timestamp: new Date().toLocaleTimeString(),
      type: 'sitemap',
      pageCategories: [{
        name: 'Related Pages',
        pages: relatedPages,
      }],
    };

    setMessages(prev => [...prev, relatedPagesMessage]);
    scrollToBottom();
  };

  // Replace fetchAIResponse with a function that calls the backend endpoint
  const fetchAIResponse = async (userMessage) => {
    try {
      const response = await fetch(api.openai, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ messages: [{ role: 'user', content: userMessage }] }),
      });
            
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        // console.error('AI API Error:', errorData);
                
        // Handle specific error cases
        if (response.status === 500 && errorData.details?.includes('GROQ_API_KEY')) {
          return 'I\'m currently not available due to a configuration issue. Please contact support or try again later.';
        }
                
        if (response.status === 503) {
          return 'I\'m temporarily unavailable. Please try again in a few moments.';
        }
                
        if (response.status === 429) {
          return 'I\'m receiving too many requests right now. Please wait a moment and try again.';
        }
                
        throw new Error(`AI service error: ${response.status}`);
      }
            
      const data = await response.json();
            
      // Check if we got a valid response
      if (data.choices && data.choices[0] && data.choices[0].message && data.choices[0].message.content) {
        return data.choices[0].message.content;
      }
            
      // Check if we got an error response
      if (data.error) {
        // console.error('AI service error:', data.error);
        return 'I\'m experiencing some technical difficulties. Please try again later or contact support.';
      }
            
      return 'Sorry, I couldn\'t process your request properly.';
    } catch (error) {
      // console.error('Error fetching AI response:', error);
            
      // Handle network errors
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        return 'I\'m unable to connect to the AI service. Please check your internet connection and try again.';
      }
            
      return 'I\'m currently experiencing technical difficulties. Please try again later or contact support for assistance.';
    }
  };

  // Update handleSendMessage to check for invalid input and use processMessage first
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim()) return;

    const userMessage = {
      text: inputMessage,
      sender: 'user',
      timestamp: new Date().toLocaleTimeString(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setInputMessage('');

    // Check if the input is valid
    if (!isValidInput(userMessage.text)) {
      // Display error message for invalid input
      setMessages((prev) => [
        ...prev,
        {
          text: 'We\'re sorry, but we\'re unable to answer your query at the moment. Please check your input and try again.',
          sender: 'ai',
          timestamp: new Date().toLocaleTimeString(),
        },
      ]);
      return;
    }

    setIsTyping(true); // Show typing indicator
    
    // First, try to get a response from our built-in processMessage function
    try {
      const builtInResponse = await processMessage(userMessage.text);
      
      // Check if we got a specific response (not the default welcome message)
      const isDefaultResponse = builtInResponse.text.includes('Welcome to the Learning Management System');
      
      if (!isDefaultResponse) {
        // We have a specific built-in response, use it
        setIsTyping(false);
        setMessages((prev) => [...prev, builtInResponse]);
        return;
      }
      
      // If it's a default response, try the external AI
      const aiResponse = await fetchAIResponse(userMessage.text);
      setIsTyping(false);
      setMessages((prev) => [
        ...prev,
        {
          text: aiResponse,
          sender: 'ai',
          timestamp: new Date().toLocaleTimeString(),
        },
      ]);
    } catch (error) {
      // If there's an error with processMessage, fall back to external AI
      const aiResponse = await fetchAIResponse(userMessage.text);
      setIsTyping(false);
      setMessages((prev) => [
        ...prev,
        {
          text: aiResponse,
          sender: 'ai',
          timestamp: new Date().toLocaleTimeString(),
        },
      ]);
    }
  };

  // Add this function to render message content with clickable links
  const renderMessageContent = (text) => {
    if (!text) return null;

    // Check if the text contains "Contact Support" and make it clickable
    if (text.includes('Contact Support')) {
      const parts = text.split(/(Contact Support)/g);
      return (
        <div>
          {parts.map((part, index) => {
            if (part === 'Contact Support') {
              return (
                <button
                  key={index}
                  className="ai-assistant-contact-support-link"
                  onClick={() => {
                    navigate('/contact-admin');
                    setMessages(prev => [...prev, {
                      text: 'Navigating to Contact Support...',
                      sender: 'ai',
                      timestamp: new Date().toLocaleTimeString(),
                      type: 'navigation',
                    }]);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#007bff',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    padding: '0',
                    font: 'inherit',
                    fontSize: 'inherit',
                  }}
                >
                  {part}
                </button>
              );
            }
            return part;
          })}
        </div>
      );
    }

    // Return regular text if no "Contact Support" found
    return text;
  };

  return (
    <>
      <button
        className="ai-assistant-button"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Open AI Learning Assistant"
      >
        <FaRobot />
      </button>

      {isOpen && (
        <div className="ai-assistant-container">
          <div className="ai-assistant-header">
            <span style={{
              fontSize: '0.6em',
              color: 'white',
              backgroundColor: 'black',
              padding: '2px 6px',
              borderRadius: '4px',
              position: 'relative',
              top: '-30px', // adjust as needed
              left: '40%', // adjust as needed
            }}>
                            Beta
            </span>
            <h3 className='ai-assistant-title'>
                            UlsaApp Assistant
                          
              <p style={{
                fontFamily: 'Arial',
                fontSize: '0.6em',
                color: 'black',
                borderRadius: '4px',
                backgroundColor: 'orange',
                padding: '2px 6px  0px 20px',
                position: 'relative',
                top: '-5px', // adjust as needed
                left: '10px',
              }}>Under development</p>
            </h3>

            <div className="ai-assistant-header-buttons">
              <button
                className="ai-assistant-related-pages-button"
                onClick={showRelatedPages}
                title="Show Related Pages">
                <FaSearch />
              </button>
              <button
                className="ai-assistant-quick-access-button"
                onClick={() => setQuickAccessVisible(!quickAccessVisible)}
                title="Quick Access">
                <FaBars />
              </button>
              <button
                className="ai-assistant-clear-button"
                onClick={clearChatHistory}
                title="Clear Chat History">
                <FaTrash />
              </button>
              <button
                className="ai-assistant-close-button"
                onClick={() => setIsOpen(false)}>
                <FaTimes />
              </button>
            </div>
          </div>

          {quickAccessVisible && renderQuickAccessPanel()}

          <div className="ai-assistant-messages">
            {messages.length === 0 && renderDailyTip()}

            {messages.map((message, index) => (
              <div
                key={index}
                className={`ai-assistant-message ${message.sender ? `ai-assistant-${message.sender}` : ''} ${message.type ? `ai-assistant-${message.type}` : ''}`}
                style={message.type === 'navigation' ? styles.navigationMessage : {}}
              >
                <div className="ai-assistant-message-content">
                  {renderMessageContent(message.text)}
                </div>

                {/* Render sitemap if available */}
                {message.type === 'sitemap' && message.pageCategories && (
                  <div style={styles.sitemapContainer}>
                    {message.pageCategories.map((category, catIndex) => (
                      <div key={catIndex} style={styles.sitemapCategory}>
                        <div style={styles.categoryName}>{category.name}</div>
                        <div style={styles.categoryPages}>
                          {category.pages
                            .filter(page => checkPageExists(page.path))
                            .map((page, pageIndex) => (
                              <button
                                key={pageIndex}
                                style={styles.pageLinkButton}
                                onClick={() => {
                                  navigate(page.path);
                                  setMessages(prev => [...prev, {
                                    text: `Navigating to ${page.name}...`,
                                    sender: 'ai',
                                    timestamp: new Date().toLocaleTimeString(),
                                    type: 'navigation',
                                  }]);
                                }}
                              >
                                {page.name}
                              </button>
                            ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {message.suggestions && message.suggestions.length > 0 && (
                  <div className="ai-assistant-message-suggestions">
                    {filterSuggestions(message.suggestions).map((suggestion, idx) => (
                      <button
                        key={idx}
                        className="ai-assistant-suggestion-button"
                        onClick={() => handleSuggestionClick(suggestion.action)}
                      >
                        {suggestion.text}
                      </button>
                    ))}
                  </div>
                )}
                <div className="ai-assistant-message-timestamp">
                  {message.timestamp}
                </div>
              </div>
            ))}
            {isTyping && (
              <div className="ai-assistant-message ai-assistant-ai">
                <div className="ai-assistant-message-content">
                  <span className="ai-assistant-typing-indicator">Typing...</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <form className="ai-assistant-input" onSubmit={handleSendMessage}>
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              placeholder="Ask me anything about your courses..."
            />
            <button type="submit">
              <FaPaperPlane />
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default AIAssistant;